import { baseApi } from "../api/baseApi";
import { toast } from "sonner";
import {
  Customer,
  CustomerNote,
  UpdateCustomerNoteRequest,
  SearchCustomer,
  CustomerSearchResponse,
  CustomerTypeEnum,
  CreateSuspectRequest,
  CreateSuspectResponse,
} from "../types/customer.types";
import { WorkItemList, WorkItemTypeEnum } from "../types/work.types";
import { SalesResponsible } from "../types/user.types";
import { CustomerCommunication } from "./communicationService";
import { Visit } from "../types/api.types";

// Additional types from myCustomerApi and businessApi
export interface CustomerAppInformationLogin {
  BrazeExternalId: string;
  BrazeExternalIdGuid: string;
  BrazeCustomer: boolean;
  BrazeCustomerSubscribed: boolean;
  HasApsisEvents: boolean;
  AppSessionId: string;
  CustomerCreated: string;
  Time: string; // or Date depending on how you handle dates in your frontend
}

export interface Segments {
  SegmentNo: number;
  SegmentName: string;
}

export interface MyCustomerResponse {
  Id: number;
  Name: string;
  GivenName: string;
  Age: number;
  CustomerId: string;
  CustomerNo: number;
  CustomerType: CustomerTypeEnum;
  CustomerAddress: string;
  SalesResponsible: SalesResponsible | null;
  CustomerAppInformationLogins: CustomerAppInformationLogin[];
  Created: string;
  LocalDb: number;
  Zynergy: number;
  Mappings: number;
}

export interface WorkItemAction {
  Id: number;
  Name: string;
  ActionDate: string | null; // ISO date string
  ActionType: WorkItemTypeEnum | null;
  ActionWorkItemNo: number | null;
  Result?: string | null;
  Text: string;
}

export interface NavCustomer {
  Id: number;
  Name: string;
  GivenName: string;
  Age: number;
  CustomerId: string;
  CustomerNo: number;
  CustomerType: CustomerTypeEnum;
  CustomerAddress: string;
  SalesResponsible: SalesResponsible | null;
  WorkItemActions: WorkItemAction[];
}

export interface MyCustomerApiResponse {
  Id: number;
  Name: string;
  CustomerId: string;
  Address: string;
  PostalCode: string;
  PostalCity: string;
  Communications: CustomerCommunication[];
  WebLogins: CustomerAppInformationLogin[];
  CustomerType: CustomerTypeEnum;
  SalesResponisble?: string | null;
  // Updated: Last contact information is now a WorkItemAction
  LastAction: WorkItemAction | null;
  LastContactType: WorkItemTypeEnum | null;

  // Customer segment information
  CustomerSegmentNo: number | null;
  CustomerSegmentName: string | null;

  // Updated: Next action is now a WorkItemAction object
  NextAction: WorkItemAction | null;

  // New fields
  B2bJourneyType: string | null;
  ActiveFVolume: number | null; // Changed from ActiveECM to ActiveFVolume as per backend model
  InvoiceDelivery?: string | null;
  DirectDebit: boolean;
  CustomerDeliveryEndDate?: string | null;
  // Orsak till leveransslut – filtrera på Id (koden), No skiljer sig per bolag
  CustomerDeliveryEndReasonNo?: number | null;
  CustomerDeliveryEndReasonId?: string | null;
  CustomerDeliveryEndReasonName?: string | null;
}

export interface MyCustomerApiRequest {
  userId?: number;
  customerTypeId?: CustomerTypeEnum;
  companyNo?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  pageSize?: number;
  page?: number;
  customerType?: "company" | "mark" | "private"; // Customer type filter
  customerGroupNo?: number; // Optional customer group number
}

export interface SuspectProspectResponse {
  SuspectProspects: MyCustomerApiResponse[];
}

export interface CustomerGroupResponse {
  Id: number;
  Name: string;
  Description: string;
  Customers?: NavCustomer[];
}

export const customerApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Individual customer operations (from customerApi.ts)
    getCustomerById: builder.query<Customer, string | number>({
      query: (id) => ({
        url: `/v1/crm/customer/${id}`,
      }),
      providesTags: ["Customer"],
    }),

    createSuspect: builder.mutation<
      CreateSuspectResponse,
      CreateSuspectRequest
    >({
      query: (request) => ({
        url: `/v1/customer/create-suspect?orgid=${request.orgId}`,
        method: "POST",
        body: {},
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Suspekt skapad.");
        } catch {
          toast.error("Det gick inte skapa suspekt, försök igen senare.");
        }
      },
      invalidatesTags: ["ProspectSuspectList"],
    }),

    getCustomersWorkItems: builder.query<WorkItemList[], string | number>({
      query: (id) => ({
        url: `/v2/crm/customer/${id}/workitems`,
      }),
      providesTags: ["Workitems"],
    }),

    searchCustomer: builder.query<CustomerSearchResponse, SearchCustomer & { fetchFromCreditsafe?: boolean }>({
      query: ({ fetchFromCreditsafe, ...data }) => ({
        url: `/v1/crm/customer/findcustomer${fetchFromCreditsafe ? "?fetchfromcreditsafe=true" : ""}`,
        method: "POST",
        body: data,
      }),
    }),

    getCustomerNote: builder.query<CustomerNote, string | number>({
      query: (customerId) => ({
        url: `/v2/crm/customer/${customerId}/note`,
      }),
      providesTags: ["CustomerNote"],
    }),

    updateCustomerNote: builder.mutation<
      CustomerNote,
      UpdateCustomerNoteRequest
    >({
      query: ({ customerId, data }) => ({
        url: `/v2/crm/customer/${customerId}/note`,
        method: "POST",
        body: { data },
      }),
      invalidatesTags: ["CustomerNote"],
    }),

    // My customers operations (from myCustomerApi.ts)
    getMyCustomers: builder.query<
      MyCustomerApiResponse[],
      MyCustomerApiRequest
    >({
      query: (params = {}) => {
        const queryParams: Record<string, string> = {};

        if (params.userId !== undefined) {
          queryParams.userId = params.userId.toString();
        }
        if (params.customerType !== undefined) {
          queryParams.customerType = params.customerType.toString();
        }
        if (params.companyNo !== undefined) {
          queryParams.companyNo = params.companyNo.toString();
        }
        if (params.customerGroupNo !== undefined) {
          queryParams.customerGroupNo = params.customerGroupNo.toString();
        }
        if (params.sortBy) {
          queryParams.sortBy = params.sortBy;
        }
        if (params.sortOrder) {
          queryParams.sortOrder = params.sortOrder;
        }
        if (params.pageSize !== undefined) {
          queryParams.pageSize = params.pageSize.toString();
        }
        if (params.page !== undefined) {
          queryParams.page = params.page.toString();
        }

        return {
          url: "/v1/business/mycustomers",
          params: queryParams,
        };
      },
      transformResponse: (response: {
        SalesCustomers: MyCustomerApiResponse[];
      }) => {
        return response.SalesCustomers;
      },
      providesTags: ["MyCustomer"],
      keepUnusedDataFor: 120,
    }),

    updateCustomerSegment: builder.mutation<
      void,
      { CustomerNos: number[]; SegmentNo: string }
    >({
      query: ({ CustomerNos, SegmentNo }) => ({
        url: "/v1/business/customer/segment",
        method: "POST",
        body: { CustomerNos, SegmentNo },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Kundsegment uppdaterat.");
        } catch {
          toast.error(
            "Det gick inte uppdatera kundsegmentet, försök igen senare."
          );
        }
      },
      invalidatesTags: ["MyCustomer", "Customer"],
    }),

    getSegments: builder.query<Segments[], void>({
      query: () => "/v1/business/customer/segments",
    }),

    getSalesEmployees: builder.query<SalesResponsible[], void>({
      query: () => "/v1/business/listSalesEmployees",
      providesTags: ["SalesEmployees"],
    }),

    updateCustomerSalesResponsible: builder.mutation<
      void,
      { customerNo: number; CustomerSalesResponsibleId: number }
    >({
      query: ({ customerNo, CustomerSalesResponsibleId }) => ({
        url: `/v1/business/customer/${customerNo}/updateCustomerSalesResponsible`,
        method: "POST",
        body: { CustomerSalesResponsibleId },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Säljare uppdaterad.");
        } catch {
          toast.error("Det gick inte uppdatera säljaren, försök igen senare.");
        }
      },
      invalidatesTags: ["MyCustomer", "Customer"],
    }),

    // Business/group operations (from businessApi.ts)
    getVisited: builder.query<Visit[], void>({
      query: () => "/v1/business/visits",
      providesTags: ["Visits"],
    }),

    getProspectSuspectList: builder.query<MyCustomerApiResponse[], void>({
      query: () => "/v1/business/getAllProspectSuspect",
      transformResponse: (response: {
        SuspectProspects: MyCustomerApiResponse[];
      }) => {
        return response.SuspectProspects;
      },
      providesTags: ["ProspectSuspectList"],
    }),

    getCustomerGroup: builder.query<
      CustomerGroupResponse[],
      {
        userNo?: number;
        customerType?: "company" | "mark" | "private";
      }
    >({
      query: (params = {}) => {
        const queryParams: Record<string, string> = {};

        if (params.userNo !== undefined) {
          queryParams.userId = params.userNo.toString();
        }
        if (params.customerType !== undefined) {
          queryParams.customerType = params.customerType.toString();
        }

        return {
          url: "/v1/business/customerGroup",
          params: queryParams,
        };
      },
      providesTags: ["CustomerGroup"],
    }),

    getCustomerGroupCustomers: builder.query<MyCustomerApiResponse[], number>({
      query: (customerGroupId) =>
        `/v1/business/customerGroup/${customerGroupId}/customers`,
      providesTags: (result, error, customerGroupId) => [
        { type: "CustomerGroup", id: customerGroupId },
      ],
    }),
  }),
});

// Export all hooks with consistent naming
export const {
  // Individual customer operations
  useGetCustomerByIdQuery,
  useCreateSuspectMutation,
  useGetCustomersWorkItemsQuery,
  useSearchCustomerQuery,
  useLazySearchCustomerQuery,
  useGetCustomerNoteQuery,
  useUpdateCustomerNoteMutation,

  // My customers operations
  useGetMyCustomersQuery,
  useUpdateCustomerSegmentMutation,
  useGetSegmentsQuery,
  useUpdateCustomerSalesResponsibleMutation,
  useGetSalesEmployeesQuery,

  // Business/group operations
  useGetVisitedQuery,
  useGetProspectSuspectListQuery,
  useGetCustomerGroupQuery,
  useGetCustomerGroupCustomersQuery,
} = customerApi;

// Re-export types for backward compatibility
export type {
  Customer,
  CustomerNote,
  UpdateCustomerNoteRequest,
  SearchCustomer,
  CustomerSearchResponse,
  CustomerModel,
  CreateSuspectRequest,
  CreateSuspectResponse,
} from "../types/customer.types";
export {
  CustomerTypeEnum,
  CustomerInvoicingDeliveryType,
} from "../types/customer.types";
export type { SalesResponsible } from "../types/user.types";
